import React, { memo, useCallback, useMemo } from 'react';

import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';

import { Button, ButtonSizes, CheckBox } from 'components/atoms';
import { Colors } from 'context';
import { ILessonJournal } from './LessonJournal';

interface IStudentsAttendance
  extends Pick<
    ILessonJournal,
    | 'students'
    | 'onCheckAllStudents'
    | 'onCheckStudent'
    | 'isAllStudentsChecked'
    | 'joinTimes'
    | 'savingIds'
    | 'onMarkJoined'
  > {}

export const StudentsAttendance = memo(
  ({
    students,
    isAllStudentsChecked,
    joinTimes,
    savingIds,
    onMarkJoined,
    onCheckAllStudents,
    onCheckStudent,
  }: IStudentsAttendance) => {
    const { t } = useTranslation();

    // spec 0013: join-click hints — chip per student + "mark joined" button
    const hasJoinHints = useMemo(
      () => Boolean(joinTimes && Object.keys(joinTimes).length),
      [joinTimes],
    );

    const showStudents = useCallback(
      () =>
        students.map(({ attendanceId, studentId, studentName, checked, isUnmarked, source }) => {
          const joinTime = studentId ? joinTimes?.[studentId] : undefined;
          const saving = Boolean(savingIds?.includes(attendanceId));

          return (
            <StudentRow key={attendanceId}>
              <CheckBoxStyled
                checked={Boolean(checked)}
                text={studentName}
                onClick={() => onCheckStudent(attendanceId, !checked)}
                // ТЗ 0026: закрытое занятие больше НЕ запирает журнал.
                // Раньше `isFinished` делал его read-only, и ошибку в
                // отметке мог исправить только сотрудник в ERP.
                disabled={saving}
              />
              {joinTime && <JoinChip>{t('lesson.join.joinedAt', { time: joinTime })}</JoinChip>}
              {/* Откуда взялась отметка. У неотмеченных чипа нет вовсе —
                  «не отмечен» и «отсутствовал» должны читаться по-разному. */}
              {!isUnmarked && source === 'auto' && (
                <SourceChip>{t('lesson.journal.source.auto')}</SourceChip>
              )}
              {!isUnmarked && source === 'manual' && (
                <SourceChip $muted>{t('lesson.journal.source.manual')}</SourceChip>
              )}
            </StudentRow>
          );
        }),
      [students, onCheckStudent, joinTimes, savingIds, t],
    );

    return (
      <StudentsAttendanceContainer>
        <HeaderRow>
          <StudentsAttendanceHeader>
            {t('lesson.journal.checkAttendances')}
          </StudentsAttendanceHeader>
          {hasJoinHints && onMarkJoined && (
            <Button
              variant="outline"
              size={ButtonSizes.sm}
              sidePaddings={16}
              onClick={onMarkJoined}>
              {t('lesson.join.markJoined')}
            </Button>
          )}
        </HeaderRow>
        {/*
          ТЗ 0026: вместо пары радио «Присутствовали все / Выборочно» — одна
          кнопка. Радио выражали режим ввода, который имел смысл, только пока
          весь журнал сохранялся одним пакетом; при мгновенном сохранении
          «выборочно» это просто ручная правка строк, а не режим.
        */}
        <BulkRow>
          <Button
            variant="outline"
            size={ButtonSizes.sm}
            sidePaddings={16}
            disabled={isAllStudentsChecked}
            onClick={onCheckAllStudents}>
            {t('lesson.journal.checkOptions.markAll')}
          </Button>
          <HintText>{t('lesson.journal.autosaveHint')}</HintText>
        </BulkRow>

        <Separator />

        <StudentsContainer studentsCount={students.length}>{showStudents()}</StudentsContainer>
      </StudentsAttendanceContainer>
    );
  },
);

const StudentsAttendanceContainer = styled.div`
  ${tablet} {
    width: 564px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const BulkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const HintText = styled.span`
  ${fontService.text({ type: TextType.extraSmall })};
  color: ${Colors.gray_dark};
`;

const StudentsAttendanceHeader = styled.h3`
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}
  margin: 0;
`;

const Separator = styled.div`
  height: 1px;
  margin: 16px 0;
  background-color: ${Colors.additional_gray};
`;

const StudentsContainer = styled.div<{ studentsCount: number }>`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${({ studentsCount }) =>
    studentsCount > 10 &&
    css`
      ${tablet} {
        display: grid;
        grid-template-columns: 1fr 1fr;
        column-gap: 24px;
      }
    `}
`;

const StudentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CheckBoxStyled = styled(CheckBox)`
  flex: 0 1 auto;
`;

const JoinChip = styled.span`
  ${fontService.text({ type: TextType.extraSmall })};
  padding: 2px 8px;
  border-radius: 10px;
  color: ${Colors.gray_dark};
  background-color: ${Colors.bg_gray};
  white-space: nowrap;
`;

const SourceChip = styled.span<{ $muted?: boolean }>`
  ${fontService.text({ type: TextType.extraSmall })};
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  color: ${({ $muted }) => ($muted ? Colors.gray_dark : Colors.black)};
  background-color: ${({ $muted }) => ($muted ? Colors.bg_gray : 'rgba(255, 218, 23, 0.24)')};
`;
